import { type FC, type PropsWithChildren, useRef } from 'react'
import { ViewMode } from '@playbooks/action-cards-utils/src'
import { useTranslation } from '../contexts/localization/Localization'
import { useAppContext } from '../contexts/AppContext'
import useMountEffect from '../hooks/useMountEffect'

// The isScrollingStarted is needed to prevent scrolling to all required not filled elements
let isScrollingStarted = false
const SCROLL_MARGIN_ADJUSTMENT = 200

const Highlighter: FC<PropsWithChildren<{ id: number; forAssessment?: boolean }>> = ({
  id,
  children,
  forAssessment,
}) => {
  const {
    highlightedIds,
    settings: { viewMode },
  } = useAppContext()
  const isHighlighted = highlightedIds.some(value => value === id)

  const isFunnelView = viewMode === ViewMode.FUNNEL

  return (
    <div className={`relative flex`}>
      {children}
      {isHighlighted && (
        <Frame id={id} highlightedIds={highlightedIds} forAssessment={!!forAssessment} isFunnelView={!!isFunnelView} />
      )}
    </div>
  )
}

const Frame: FC<{
  highlightedIds: number[]
  isFunnelView: boolean
  forAssessment: boolean
  id: number
}> = ({ highlightedIds, isFunnelView, forAssessment, id }) => {
  const { text } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)

  useMountEffect(() => {
    if (!ref.current || !highlightedIds.length || isScrollingStarted) return

    isScrollingStarted = true

    const container = isFunnelView
      ? ref.current.closest(`[data-separator-scope]`)
      : ref.current.closest(`[data-role="data-action-cards-sides-scroll-container"]`)

    const frame = container!.querySelector('[data-highlighter-container]')!

    const { top } = frame.getBoundingClientRect()

    const contScrollTop = container!.scrollTop

    container?.scrollTo({ top: contScrollTop + top - SCROLL_MARGIN_ADJUSTMENT })

    setTimeout(() => (isScrollingStarted = false), 100)
  })

  return (
    <div
      ref={ref}
      data-highlighter-container
      data-testid={`highlighter_${id}`}
      className={`
            absolute h-full w-full border-solid z-1
            ${
              forAssessment
                ? 'border-b-red-500 border-transparent border-b-2 top-0.75 min-w-50'
                : 'border-red-500 border-b-4 border rounded-[6px] top-0'
            }
            text-red-500 pointer-events-none
          `}
    >
      <span
        className={`absolute text-[12px] right-2  ${
          isFunnelView ? (forAssessment ? '-bottom-4.25' : 'bottom-0') : forAssessment ? '-bottom-5' : 'bottom-0'
        }`}
      >
        {forAssessment ? text('assessmentRequiredLabel') : text('questionRequiredLabel')}
      </span>
    </div>
  )
}
export default Highlighter
